import type { SettlementDirection, SettlementStatus } from '@cuito/types';
import { readErrorCode, readServerMessage } from '../../lib/apiError';

/**
 * 结算的方向、状态与错误文案的**唯一出处**（UI 规范 554 行的 `/settlements`）。
 *
 * **本文件不含任何判据**：方向与状态一律用服务端下发的 `direction` / `status`，
 * 这里只做「枚举 → 中文」；错误文案按**业务码**取（比 HTTP 状态可靠：409 有三个不同的码）。
 *
 * 未知取值**显示原文**（不 `undefined`）—— 与 `refundLabels.ts` 的守卫同款。
 */
export const SETTLEMENT_DIRECTION_TEXT: Record<SettlementDirection, string> = {
  FORWARD: '正向结算',
  REVERSE: '反向结算',
};

export const SETTLEMENT_STATUS_TEXT: Record<SettlementStatus, string> = {
  PENDING: '待结算',
  SETTLED: '已结算',
};

export const SETTLEMENT_DIRECTION_CLASS: Record<SettlementDirection, string> = {
  FORWARD: 'border-black bg-white text-black',
  REVERSE: 'border-black bg-black text-white',
};

export const SETTLEMENT_STATUS_CLASS: Record<SettlementStatus, string> = {
  PENDING: 'border-black bg-white text-black',
  SETTLED: 'border-black bg-gray-100 text-gray-700',
};

/**
 * 七个错误码的逐字文案（UI 规范 554 行的错误态那一栏）。
 *
 * ⚠️ **只有这七个**：其余码（`TRANSFER_NOT_RECEIVED` / `SETTLEMENT_ALREADY_LINE` /
 * `SETTLEMENT_SCOPE_REQUIRED` / `SETTLEMENT_PERIOD_INVALID` / `TENANT_NOT_FOUND` /
 * `LOCATION_NOT_FOUND`）UI 规范**没有给文案** ⇒ 一律退回服务端 `message`，
 * 前端不自己发明一句话（**待裁决 D-17**）。
 */
export const SETTLEMENT_ERROR_TEXT: Record<string, string> = {
  SETTLEMENT_ALREADY_SETTLED: '结算单已结算，不可重复操作',
  SETTLEMENT_ALREADY_EXISTS: '该期间已有结算单',
  SETTLEMENT_NO_DATA: '暂无可结算数据',
  DESTINATION_ALREADY_SET: '该单据的去向已选定',
  NEXT_EVENT_REQUIRED: '请选择下一场展会',
  WAREHOUSE_REQUIRED: '请选择仓库',
  TRANSFER_NO_REMAINING_STOCK: '该展位已无剩余库存',
};

function isDirection(value: string): value is SettlementDirection {
  return value === 'FORWARD' || value === 'REVERSE';
}

function isStatus(value: string): value is SettlementStatus {
  return value === 'PENDING' || value === 'SETTLED';
}

export function settlementDirectionText(direction: string): string {
  return isDirection(direction) ? SETTLEMENT_DIRECTION_TEXT[direction] : direction;
}

export function settlementStatusText(status: string): string {
  return isStatus(status) ? SETTLEMENT_STATUS_TEXT[status] : status;
}

export function settlementDirectionClass(direction: string): string {
  return isDirection(direction) ? SETTLEMENT_DIRECTION_CLASS[direction] : 'border-gray-400 bg-white text-gray-500';
}

export function settlementStatusClass(status: string): string {
  return isStatus(status) ? SETTLEMENT_STATUS_CLASS[status] : 'border-gray-400 bg-white text-gray-500';
}

/**
 * 错误 → 文案。优先级：**码文案 > 服务端 `message` > 兜底**。
 *
 * 码优先的理由：UI 规范为这七个码逐字定了中文，而服务端的 `message` 是给日志与调试的
 * （同一句英文在不同版本里会漂），财务界面上必须是那句定稿的中文。
 */
export function settlementErrorText(error: unknown, fallback: string): string {
  const code = readErrorCode(error);
  if (code !== null && SETTLEMENT_ERROR_TEXT[code] !== undefined) return SETTLEMENT_ERROR_TEXT[code];
  return readServerMessage(error) ?? fallback;
}

/** 生成失败的兜底文案（UI 规范 554 行逐字） */
export const GENERATE_FAILED_TEXT = '生成失败，请重试';
/** 取数失败的兜底文案（冻结文档 §五 逐字） */
export const LOAD_FAILED_TEXT = '加载失败，请重试';
